/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react'
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grow,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  Controller,
  useWatch,
  Control,
  UseFormTrigger,
  UseFormGetValues,
  UseFormResetField,
  useFieldArray,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useCreatePropertyAdStore } from 'store'
import shallow from 'zustand/shallow'
import { FormValues } from '..'
import useDebounce from 'hooks/useDebounce'
import Price from '../common/Price'
import {
  PROPERTY_AD_STATUS_OPTIONS,
  ADMIN_PROPERTY_AD_STATUS_OPTIONS,
} from '../constants'
import EstateCode from '../common/EstateCode'
import {
  EMPTY_OPTION,
  INTEREST_OPTIONS,
} from 'store/CreatePropertyAdSlice/constants'
import KeycloakService from 'services/KeycloakService'
import { EstateAgent } from 'types'

interface Props {
  control: Control<FormValues>
  trigger: UseFormTrigger<FormValues>
  getValues: UseFormGetValues<FormValues>
  resetField: UseFormResetField<FormValues>
  agent?: EstateAgent
}

const Step4 = (props: Props) => {
  const { control, trigger } = props
  const { t } = useTranslation()

  const [uncompleteStep, invalidateStep] = useCreatePropertyAdStore(
    (state) => [state.uncompleteStep, state.invalidateStep],
    shallow
  )

  const firstRender = React.useRef<unknown>(true)
  React.useEffect(() => {
    if (firstRender.current) firstRender.current = false
  }, [])

  const watch = useWatch({ control, name: 'adAttributes' })

  const checkValidStatus = async () => {
    if (!firstRender.current) {
      const isValid = await trigger([
        'adAttributes.titles',
        'adAttributes.descriptions',
      ])
      if (!isValid) {
        uncompleteStep('adAttributes')
        invalidateStep('adAttributes')
      }
    }
  }

  const debouncedCheck = useDebounce(checkValidStatus, 200)

  React.useEffect(() => {
    debouncedCheck()
  }, [watch, debouncedCheck])

  const { fields } = useFieldArray({
    control,
    name: 'adAttributes.descriptions',
    keyName: 'formId',
  })

  const { fields: titles } = useFieldArray({
    control,
    name: 'adAttributes.titles',
    keyName: 'formId',
  })

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack spacing={2}>
          <Typography sx={{ marginBottom: 4, fontSize: 20 }}>
            {t('manageEstateAd.steps.adAttributes.description')}
            <Typography
              component="span"
              display="block"
              color="error"
              sx={{ marginBottom: 4 }}
            >
              {`(*) ${t(
                'manageEstateAd.steps.estateDescription.requiredFields'
              )}`}
            </Typography>
          </Typography>

          <Controller
            name="adAttributes.interest"
            control={control}
            rules={{
              required: t(`manageEstateAd.fieldRequired`) as unknown as string,
            }}
            render={({ field, fieldState: { error } }) => (
              <FormControl>
                <FormLabel sx={{ whiteSpace: 'pre' }}>
                  {t('manageEstateAd.steps.estateCategory.availableFor')}
                  <Typography
                    component="span"
                    display="inline-block"
                    color="error"
                  >
                    {` (*)`}
                  </Typography>
                </FormLabel>
                <RadioGroup {...field}>
                  {INTEREST_OPTIONS.map((item) => (
                    <FormControlLabel
                      key={item.value}
                      value={item.value}
                      control={<Radio />}
                      label={t(item.label) as unknown as string}
                    />
                  ))}
                </RadioGroup>
                <Grow in={Boolean(error)}>
                  <FormHelperText error={Boolean(error)}>
                    {error?.message}
                  </FormHelperText>
                </Grow>
              </FormControl>
            )}
          />

          <Price control={control} />

          <EstateCode control={control} />

          <Stack spacing={2}>
            <Typography color="textSecondary" sx={{ fontSize: 18 }}>
              {t('manageEstateAd.steps.adAttributes.fields.description')}
            </Typography>
            {fields.map((field, index) => (
              <Controller
                key={field.formId}
                name={`adAttributes.descriptions.${index}.text` as const}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t(
                      'dashboard.pages.createAgent.partners.fields.name'
                    )}
                    minRows={5}
                    multiline={true}
                    error={!!error}
                    helperText={error?.message}
                    {...field}
                  />
                )}
              />
            ))}
          </Stack>

          <Stack spacing={2}>
            <Typography color="textSecondary" sx={{ fontSize: 18 }}>
              {t('manageEstateAd.steps.adAttributes.fields.title')}
            </Typography>
            {titles.map((field, index) => (
              <Controller
                key={field.formId}
                name={`adAttributes.titles.${index}.text` as const}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('manageEstateAd.steps.adAttributes.fields.title')}
                    minRows={5}
                    multiline={true}
                    error={!!error}
                    helperText={error?.message}
                    {...field}
                  />
                )}
              />
            ))}
          </Stack>

          <>
            <Stack spacing={2}>
              <Typography
                color="textSecondary"
                sx={{ fontSize: 18, whiteSpace: 'pre' }}
              >
                {t('manageEstateAd.steps.adAttributes.fields.status')}
                <Typography
                  component="span"
                  display="inline-block"
                  color="error"
                >
                  {` (*)`}
                </Typography>
              </Typography>
              <Controller
                name="adAttributes.status"
                control={control}
                rules={{
                  required: t(
                    `manageEstateAd.fieldRequired`
                  ) as unknown as string,
                }}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth>
                    <Select
                      displayEmpty
                      fullWidth
                      {...field}
                      error={Boolean(error)}
                      MenuProps={{
                        sx: {
                          '& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected':
                            {
                              bgcolor: 'primary.main',
                            },
                        },
                      }}
                    >
                      <MenuItem value={EMPTY_OPTION.value}>
                        {t(EMPTY_OPTION.label)}
                      </MenuItem>
                      {KeycloakService.isAdmin()
                        ? ADMIN_PROPERTY_AD_STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.label} value={option.value}>
                              {t(option.label)}
                            </MenuItem>
                          ))
                        : null}
                      {KeycloakService.isAgent()
                        ? PROPERTY_AD_STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.label} value={option.value}>
                              {t(option.label)}
                            </MenuItem>
                          ))
                        : null}
                    </Select>
                    <Grow in={Boolean(error)}>
                      <FormHelperText error={Boolean(error)}>
                        {error?.message}
                      </FormHelperText>
                    </Grow>
                  </FormControl>
                )}
              />
            </Stack>
          </>
          {/* DO NOT REMOVE - to be  readded in the future */}
          {/* <Stack spacing={2}>
            <Typography
              color="textSecondary"
              sx={{ fontSize: 18, whiteSpace: 'pre' }}
            >
              {t('manageEstateAd.steps.adAttributes.fields.contact')}
              <Typography component="span" display="inline-block" color="error">
                {` (*)`}
              </Typography>
            </Typography>
            <Controller
              name="adAttributes.agentCInfo"
              control={control}
              rules={{
                required: t(`manageEstateAd.fieldRequired`) as string,
              }}
              defaultValue={''}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth>
                  <Select
                    displayEmpty
                    fullWidth
                    {...field}
                    error={Boolean(error)}
                    MenuProps={{
                      sx: {
                        '& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected': {
                          bgcolor: 'primary.main',
                        },
                      },
                    }}
                  >
                    <MenuItem value={EMPTY_OPTION.value}>
                      {t(EMPTY_OPTION.label)}
                    </MenuItem>
                    {agent?.cInfos?.map((option) => (
                      <MenuItem key={option.id!} value={option.id!}>
                        {option?.contactName}
                      </MenuItem>
                    ))}
                  </Select>
                  <Grow in={Boolean(error)}>
                    <FormHelperText error={Boolean(error)}>
                      {error?.message}
                    </FormHelperText>
                  </Grow>
                </FormControl>
              )}
            />
          </Stack> */}
        </Stack>
      </Box>
    </>
  )
}

export default React.memo(Step4)
